import { Box } from '@chakra-ui/react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import { Condition } from 'Component/others';
import { ChevronLeftIcon, ChevronRightIcon } from 'Asset/icon';
import { useLoader } from 'Store/loader';
import { useToast } from 'Store/toast';

import { useScheduleGenerateState } from 'View/schedule-generate/state';
import CalendarContent from 'View/schedule-generate/component/calendar-content';
import onDropCalendar from 'View/schedule-generate/helper/onDropCalendar';
import getWeekInfo from 'View/schedule-generate/helper/getWeekInfo';
import { getPeriodWeek } from 'View/schedule-generate/helper/getPeriodDay';
import CalendarDay from 'View/schedule-generate/component/calendar-day';
import CalendarHead from 'View/schedule-generate/component/calendar-head';

import styles from './styles.module.css';

const GenerateResultCalendar = () => {
  const { t } = useTranslation('schedule-generate');
  const { onShowLoader, onResetLoader } = useLoader();
  const { onOpenToast } = useToast();
  const { accounts, updateState, selectedTab, generateResultData, hostsInRulesets } = useScheduleGenerateState();
  const accountIndex = accounts.findIndex((account) => account.type === selectedTab);
  const { metadata } = generateResultData;

  const selectedYearMonth = metadata?.yearMonth;
  const today = moment();
  const anchorYear = parseInt(selectedYearMonth?.slice(0, 4));
  const anchorMonthIdx = parseInt(selectedYearMonth?.slice(5, 7)) - 1; // 0-indexed
  const periodStart = moment({ year: anchorYear, month: anchorMonthIdx, day: 16 }).startOf('day');
  const periodEnd = moment({ year: anchorYear, month: anchorMonthIdx + 1, day: 15 }).endOf('day');
  const isCurrentPeriod = today.isSameOrAfter(periodStart) && today.isSameOrBefore(periodEnd);

  const firstWeek = isCurrentPeriod ? getPeriodWeek(today.date(), selectedYearMonth) : 1;

  const [showWeek, setShowWeek] = useState(firstWeek);

  const weekInfo = getWeekInfo({ selectedYearMonth, showWeek, firstWeek });

  const onChangeShowWeek = (value) => {
    onShowLoader({ variant: 'loader-primary' });

    setTimeout(() => onResetLoader(), 500);

    setShowWeek(value);
  };

  useEffect(() => {
    return monitorForElements({
      onDrop({ source, location }) {
        onDropCalendar({ location, source, updateState, showWeek, accounts, accountIndex, onOpenToast, hostsInRulesets, generateResultData, t });
      },
    });
  }, [updateState, showWeek, accountIndex, accounts, onOpenToast, hostsInRulesets, generateResultData, t]);

  return (
    <Box className={styles['generate-result-calendar__wrapper']}>
      {/* Left Chevron */}

      <Condition statement={!weekInfo?.isFirstWeek} fallback={<Box width={'24px'} />}>
        <ChevronLeftIcon
          cursor="pointer"
          color="var(--color-primary80)"
          size="24px"
          onClick={() => onChangeShowWeek(showWeek - 1)}
          data-testid="decrease-week-button"
        />
      </Condition>

      {/* Body */}

      <Box className={styles['generate-result-calendar__container']}>
        <CalendarHead showWeek={showWeek} weekInfo={weekInfo} firstWeek={firstWeek} />

        <Box className={styles['generate-result-calendar__section--content']}>
          <CalendarDay showWeek={showWeek} weekInfo={weekInfo} />
          <CalendarContent showWeek={showWeek} lastDate={weekInfo?.lastDate} />
        </Box>
      </Box>

      {/* Right Chevron */}
      <Condition statement={!weekInfo?.isLastWeek} fallback={<Box width={'24px'} />}>
        <ChevronRightIcon
          cursor="pointer"
          color="var(--color-primary80)"
          size="24px"
          onClick={() => onChangeShowWeek(showWeek + 1)}
          data-testid="increase-week-button"
        />
      </Condition>
    </Box>
  );
};

export default GenerateResultCalendar;
